import { Alert, flash, Label, Switch } from "@xola/ui-kit";
import { SwitchField } from "common";
import { FormikProps } from "formik";
import { map } from "lodash";
import React, { forwardRef, Ref } from "react";
import { useMutation } from "react-query";
import {
    EnabledTabs,
    EventData,
    EventDataCopy,
    FeatureRole,
    Permission,
    ReservationData,
    ReservationDataCopy,
    RosterExportPreference,
    RosterPreference,
    Seller,
    SellerMeta,
    WaiverData,
    WaiverDataCopy,
} from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { hasFeature } from "../../../models/Seller";
import { IsGranted } from "../../Common/IsGranted";
import { XolaFormik } from "../../Common/XolaFormik";

export const RosterPreferences = forwardRef((props, ref: Ref<FormikProps<Seller>>) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const { mutateAsync: updateMeta } = useMutation(async (values: SellerMeta) => {
        if (seller.meta !== values) {
            await api.post(`/sellers/${seller.id}/meta`, values);
        }
    });

    const { mutateAsync: updateRoster } = useMutation(async (values: RosterPreference) => {
        if (seller.preferences.roster !== values) {
            await api.post(`/users/${seller.id}/preferences/roster`, values);
        }
    });

    const { mutateAsync: updateRosterExport } = useMutation(async (values: RosterExportPreference) => {
        if (seller.preferences.rosterExport !== values) {
            await api.post(`/users/${seller.id}/preferences/rosterExport`, values);
        }
    });

    const handleSubmit = async (seller: Seller) => {
        await Promise.all([
            updateMeta(seller.meta),
            updateRoster(seller.preferences.roster),
            updateRosterExport(seller.preferences.rosterExport),
        ]);

        flash.success("Roster settings saved");
        invalidate("sellers", seller.id);
    };

    return (
        <XolaFormik enableReinitialize innerRef={ref} initialValues={seller} onSubmit={handleSubmit}>
            {({ values, setFieldValue, isSubmitting }) => {
                const { enabledTabs } = values.preferences.rosterExport;
                return (
                    <fieldset className="space-y-4" disabled={isSubmitting}>
                        <h3 className="mb-4">Roster Export</h3>

                        <div className="flex items-baseline space-x-2">
                            <Label>Export Event Data</Label>
                            <Switch
                                isChecked={enabledTabs.includes(EnabledTabs.SUMMARY)}
                                size="small"
                                onChange={() => {
                                    setFieldValue(
                                        "preferences.rosterExport.enabledTabs",
                                        toggleArrayValue(enabledTabs, EnabledTabs.SUMMARY),
                                    );
                                }}
                            />
                        </div>

                        <Alert color="primary" className="mt-2 py-4 px-6">
                            Select data points that should be included in roster exports. Rosters are exported in .xlsx
                            format
                        </Alert>

                        {map(EventDataCopy, (value: EventData, label: string) => {
                            const { eventData } = values.preferences.rosterExport;
                            if (
                                value === EventData.WAIVERS_SIGNED &&
                                !isGranted(Permission.MODIFY_ROSTERS_WAIVER_EXPORT_DATA)
                            ) {
                                return null;
                            }

                            if (value === EventData.GUIDES && !hasFeature(seller, FeatureRole.GUIDE_MANAGER)) {
                                return null;
                            }

                            return (
                                <div key={value} data-testid="roster-tab-event-section">
                                    <SwitchField
                                        isChecked={eventData.includes(value)}
                                        isDisabled={!enabledTabs.includes(EnabledTabs.SUMMARY)}
                                        onChange={() => {
                                            setFieldValue(
                                                "preferences.rosterExport.eventData",
                                                toggleArrayValue(eventData, value),
                                            );
                                        }}
                                    >
                                        {label}
                                    </SwitchField>
                                </div>
                            );
                        })}

                        <div className="flex items-baseline space-x-2">
                            <Label>Export Reservation Data</Label>
                            <Switch
                                isChecked={enabledTabs.includes(EnabledTabs.ROSTER)}
                                size="small"
                                onChange={() => {
                                    setFieldValue(
                                        "preferences.rosterExport.enabledTabs",
                                        toggleArrayValue(enabledTabs, EnabledTabs.ROSTER),
                                    );
                                }}
                            />
                        </div>

                        <Alert color="primary" className="mt-2 py-4 px-6">
                            Select data points that should be included in roster exports. Rosters are exported in .xlsx
                            format
                        </Alert>

                        {map(ReservationDataCopy, (value: ReservationData, label: string) => {
                            const { reservationData } = values.preferences.rosterExport;
                            if (
                                value === ReservationData.WAIVERS &&
                                !isGranted(Permission.MODIFY_ROSTERS_WAIVER_EXPORT_DATA)
                            ) {
                                return null;
                            }

                            if (
                                value === ReservationData.EQUIPMENT_USED &&
                                !hasFeature(seller, FeatureRole.SELLER_INVENTORY)
                            ) {
                                return null;
                            }

                            return (
                                <div key={value} data-testid="roster-tab-reservation-section">
                                    <SwitchField
                                        isChecked={reservationData.includes(value)}
                                        isDisabled={
                                            !enabledTabs.includes(EnabledTabs.ROSTER) ||
                                            value === ReservationData.QUESTIONNAIRE
                                        }
                                        onChange={() => {
                                            setFieldValue(
                                                "preferences.rosterExport.reservationData",
                                                toggleArrayValue(reservationData, value),
                                            );
                                        }}
                                    >
                                        {label}
                                    </SwitchField>
                                </div>
                            );
                        })}

                        <IsGranted permissions={Permission.MODIFY_ROSTERS_WAIVER_EXPORT_DATA}>
                            <div className="flex items-baseline space-x-2">
                                <Label>Export Waivers Data</Label>
                                <Switch
                                    isChecked={enabledTabs.includes(EnabledTabs.WAIVERS)}
                                    size="small"
                                    onChange={() => {
                                        setFieldValue(
                                            "preferences.rosterExport.enabledTabs",
                                            toggleArrayValue(enabledTabs, EnabledTabs.WAIVERS),
                                        );
                                    }}
                                />
                            </div>

                            <Alert color="primary" className="mt-2 py-4 px-6">
                                Select data points that should be included in roster exports. Rosters are exported in
                                .xlsx format
                            </Alert>

                            {map(WaiverDataCopy, (value: WaiverData, label: string) => {
                                const { waiversData } = values.preferences.rosterExport;

                                return (
                                    <div key={value} data-testid="roster-tab-waivers-section">
                                        <SwitchField
                                            isChecked={waiversData.includes(value)}
                                            isDisabled={!enabledTabs.includes(EnabledTabs.WAIVERS)}
                                            onChange={() => {
                                                setFieldValue(
                                                    "preferences.rosterExport.waiversData",
                                                    toggleArrayValue(waiversData, value),
                                                );
                                            }}
                                        >
                                            {label}
                                        </SwitchField>
                                    </div>
                                );
                            })}
                        </IsGranted>
                    </fieldset>
                );
            }}
        </XolaFormik>
    );
});

const toggleArrayValue = (array: string[], value: string) => {
    return array.includes(value) ? array.filter((item) => item !== value) : [...array, value];
};
